/**
 * Batch Transport
 *
 * Buffers log entries and ships them in batches to a backend endpoint
 * for CloudWatch ingestion. Uses navigator.sendBeacon on page unload.
 */

import type { FrontendLogEntry, LogTransport } from '../types';

const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 5000;

export class BatchTransport implements LogTransport {
  private buffer: FrontendLogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private readonly endpoint: string;
  private readonly batchSize: number;
  private readonly flushIntervalMs: number;

  constructor(config: {
    endpoint: string;
    batchSize?: number;
    flushIntervalMs?: number;
  }) {
    this.endpoint = config.endpoint;
    this.batchSize = config.batchSize ?? DEFAULT_BATCH_SIZE;
    this.flushIntervalMs = config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;

    this.startAutoFlush();
    this.registerUnloadHandler();
  }

  write(entry: FrontendLogEntry): void {
    this.buffer.push(entry);

    if (this.buffer.length >= this.batchSize) {
      void this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0) {
      return;
    }

    const entries = this.buffer.splice(0, this.batchSize);

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      });

      if (!response.ok) {
        // Re-queue entries on failure (drop if buffer is too large to prevent memory leak)
        if (this.buffer.length < this.batchSize * 3) {
          this.buffer.unshift(...entries);
        }
      }
    } catch {
      // Network error — re-queue if buffer isn't too large
      if (this.buffer.length < this.batchSize * 3) {
        this.buffer.unshift(...entries);
      }
    }
  }

  destroy(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    // Best-effort flush remaining entries
    this.flushViaSendBeacon();
  }

  private startAutoFlush(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.flushTimer = setInterval(() => {
      void this.flush();
    }, this.flushIntervalMs);
  }

  private registerUnloadHandler(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushViaSendBeacon();
      }
    });
  }

  private flushViaSendBeacon(): void {
    if (this.buffer.length === 0) {
      return;
    }

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const payload = JSON.stringify({ entries: this.buffer });
      navigator.sendBeacon(this.endpoint, payload);
      this.buffer = [];
    }
  }
}
